import { useQuery } from '@tanstack/react-query';
import { QueryKeys } from '../../../constants/queryKeys';
import { ActivityEndpoints } from '../../../constants/endpoints/activityEndpoints.ts';
import { getAxios } from '../../../lib/axios';
import {
  ActivityBookingFlagsModel,
  ActivityDuplicateGroupModel,
  ActivityStatusSummaryModel,
  ActivityTravellerOptionsModel
} from './activityStatusModels';

const activityStatusStaleTime = 30_000;

/**
 * Lightweight activity shell data for tab labels and header/status display.
 *
 * This query should stay cheap enough to run for every open activity tab.
 * Keep traveller lists, duplicate groups, hotel rates, payment cards, and
 * validation-heavy flags in the targeted status queries below.
 */
export function useActivityStatusSummaryQuery(activityId: number | undefined) {
  return useQuery<ActivityStatusSummaryModel>({
    queryKey: [
      QueryKeys.editActivity(activityId ?? -1),
      ActivityEndpoints.activityStatusSummary(activityId ?? -1),
      activityId
    ],
    queryFn: async () => (await getAxios().get(`${ActivityEndpoints.activityStatusSummary(activityId ?? -1)}`)).data,
    enabled: !!activityId && !isNaN(activityId),
    retry: false,
    staleTime: activityStatusStaleTime
  });
}

/**
 * Edit-flow gatekeeping and booking/payment state.
 *
 * Use this for form locks, toolbar enablement, hotel and itinerary state,
 * payment readiness, reporting validation, approval flags, unread email flags,
 * and activity issue indicators.
 */
export function useActivityBookingFlagsQuery(activityId: number | undefined) {
  return useQuery<ActivityBookingFlagsModel>({
    queryKey: [
      QueryKeys.editActivity(activityId ?? -1),
      ActivityEndpoints.activityBookingFlags(activityId ?? -1),
      activityId
    ],
    queryFn: async () => (await getAxios().get(`${ActivityEndpoints.activityBookingFlags(activityId ?? -1)}`)).data,
    enabled: !!activityId && !isNaN(activityId),
    retry: false,
    staleTime: activityStatusStaleTime
  });
}

/**
 * Traveller identity and select-option data for activity edit flows.
 *
 * Use this where traveller lists, traveller selects, requested/approved booker
 * ids, or traveller counts are needed.
 */
export function useActivityTravellerOptionsQuery(activityId: number | undefined) {
  return useQuery<ActivityTravellerOptionsModel>({
    queryKey: [
      QueryKeys.editActivity(activityId ?? -1),
      ActivityEndpoints.activityTravellerOptions(activityId ?? -1),
      activityId
    ],
    queryFn: async () => (await getAxios().get(`${ActivityEndpoints.activityTravellerOptions(activityId ?? -1)}`)).data,
    enabled: !!activityId && !isNaN(activityId),
    retry: false,
    staleTime: activityStatusStaleTime
  });
}

/**
 * Duplicate booking group data.
 *
 * Use this for duplicate badges, duplicate/split confirmations, and related
 * booking displays. Keep duplicate group lookups out of the summary query.
 */
export function useActivityDuplicateGroupQuery(activityId: number | undefined) {
  return useQuery<ActivityDuplicateGroupModel>({
    queryKey: [
      QueryKeys.editActivity(activityId ?? -1),
      ActivityEndpoints.activityDuplicateGroup(activityId ?? -1),
      activityId
    ],
    queryFn: async () => (await getAxios().get(`${ActivityEndpoints.activityDuplicateGroup(activityId ?? -1)}`)).data,
    enabled: !!activityId && !isNaN(activityId),
    retry: false,
    staleTime: activityStatusStaleTime
  });
}
